import React, { useEffect, useState } from "react";
import SidebarNav from "../sidebar";
import { useParams, useHistory, Link } from "react-router-dom";
import { getCoinById, getUsersByCoinIdAndWalletType } from "../../../service/api";
import { Tag, Spin, Table } from "antd";
import { FiArrowLeft } from "react-icons/fi";
import moment from "moment";
import { toast } from "react-toastify";
import Select from "react-select";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import { triggerDownload } from "../../../config/downloadUtils";
import { sanitizeInput } from "../../../config/validation";

const formatDateTime = (val) =>
  val ? moment(val).format("DD/MM/YYYY HH:mm") : "—";

const walletTypeOptions = [
  { label: "All Wallets", value: "ALL" },
  { label: "Spot", value: "SPOT" },
  { label: "Funding", value: "FUNDING" },
];

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    height: 40,
    minWidth: 130,
    borderRadius: 4,
    border: "1px solid #ced4da",
    fontSize: 14,
    backgroundColor: "#fff",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ── ALL_COLUMNS definition for useTableSettings ──────────────────────────────

const ALL_COLUMNS = [
  {
    key: "user",
    title: "User",
    defaultVisible: true,
    render: (r) => {
      const name = [r?.userDetails?.firstName, r?.userDetails?.lastName].filter(Boolean).join(" ");
      return name || r?.userId || r?.user || "—";
    },
  },
  {
    key: "email",
    title: "Email",
    defaultVisible: true,
    render: (r) => r?.userDetails?.email || "—",
  },
  {
    key: "walletType",
    title: "Type",
    defaultVisible: true,
    render: (r) => {
      if (r?.walletType === "SPOT") return <Tag color="blue">Spot</Tag>;
      if (r?.walletType === "FUNDING") return <Tag color="purple">Funding</Tag>;
      return "—";
    },
  },
  {
    key: "amount",
    title: "Amount",
    defaultVisible: true,
    render: (r) => r?.amount != null ? r.amount : "—",
  },
  {
    key: "reserved",
    title: "Reserved",
    defaultVisible: true,
    render: (r) => r?.reserved != null ? r.reserved : "—",
  },
];

// ── reusable pieces ──────────────────────────────────────────────────────────

const InfoRow = ({ label, children, mono }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "9px 0", borderBottom: "1px solid #f0f0f0" }}>
    <span style={{ fontSize: 13, color: "#8c8c8c", fontWeight: 500, minWidth: 150 }}>{label}</span>
    <span style={{ fontSize: 13, color: "#262626", textAlign: "right", fontFamily: mono ? "monospace" : undefined, wordBreak: "break-all", maxWidth: "58%" }}>
      {children ?? "—"}
    </span>
  </div>
);

const Card = ({ title, icon, children, style }) => (
  <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 10, marginBottom: 20, overflow: "hidden", ...style }}>
    {title && (
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 8 }}>
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
        <span style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>{title}</span>
      </div>
    )}
    <div style={{ padding: "4px 20px 12px" }}>{children}</div>
  </div>
);

const StatCard = ({ label, value, color }) => (
  <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 10, padding: "16px 20px", flex: "1 1 160px", minWidth: 0 }}>
    <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 6, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: color || "#262626" }}>{value ?? "—"}</div>
  </div>
);

// ── main component ───────────────────────────────────────────────────────────

const CoinDetail = () => {
  const { id } = useParams();
  const history = useHistory();

  const [coin, setCoin] = useState(null);
  const [coinLoading, setCoinLoading] = useState(true);
  const [coinError, setCoinError] = useState(null); // message string when fetch fails or returns no coin

  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersLoading, setUsersLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // filters
  const [walletType, setWalletType] = useState(walletTypeOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "coin-detail-users",
    allColumns: ALL_COLUMNS,
  });

  // fetch coin
  useEffect(() => {
    if (!id) return;
    setCoinLoading(true);
    setCoinError(null);
    setCoin(null);
    getCoinById(id)
      ?.then((res) => {
        // Backend signals "not found" with { status: false, message, data: null }.
        if (res && res.status === false) {
          setCoin(null);
          setCoinError(res?.message || "Coin not found");
          return;
        }
        const d = res?.data ?? res;
        const found = Array.isArray(d) ? d[0] : d;
        if (!found) {
          setCoin(null);
          setCoinError("Coin not found");
          return;
        }
        setCoin(found);
      })
      .catch((e) => {
        setCoin(null);
        setCoinError(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load coin details",
        );
      })
      .finally(() => setCoinLoading(false));
  }, [id]);

  // build query params
  const buildQueryBody = (overrides = {}) => {
    const body = { page: usersPage, sizePerPage: pageSize };
    if (searchTerm) body.search = searchTerm;
    return { ...body, ...overrides };
  };

  const fetchUsers = (page = usersPage) => {
    if (!id || !walletType) return;
    setUsersLoading(true);
    getUsersByCoinIdAndWalletType(id, walletType.value, buildQueryBody({ page }))
      .then((res) => {
        const d = res?.data ?? res;
        const resultList = d?.result ?? d?.docs ?? d ?? [];
        setUsers(Array.isArray(resultList) ? resultList : (resultList?.data ?? []));
        setUsersTotal(d?.total ?? d?.length ?? 0);
      })
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    // Skip the users fetch until we know the coin exists — otherwise we
    // hit the API with a coinId the backend doesn't have.
    if (!coin) return;
    fetchUsers(usersPage);
  }, [id, coin, usersPage, pageSize, walletType, searchTerm]);

  // reset to page 1 on filter/pageSize change
  useEffect(() => {
    setUsersPage(1);
  }, [pageSize, walletType, searchTerm]);

  const handleDownload = (format) => {
    setDownloadLoading(true);
    getUsersByCoinIdAndWalletType(id, walletType.value, buildQueryBody({ page: 1, sizePerPage: usersTotal || 1000 }))
      .then((res) => {
        const d = res?.data ?? res;
        const resultList = d?.result ?? d?.docs ?? d ?? [];
        const result = Array.isArray(resultList) ? resultList : (resultList?.data ?? []);
        if (!result.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
        const cols = ALL_COLUMNS.filter((c) => visibleColumnKeys.includes(c.key));
        triggerDownload(format, result, cols, `coin-users-${id}`);
      })
      .catch(() => toast.error("Download failed", { toastId: 'download-failed' }))
      .finally(() => setDownloadLoading(false));
  };

  const clearFilters = () => {
    setWalletType(walletTypeOptions[0]);
    setSearchTerm("");
  };

  const coinSymbol = coin?.symbol ?? "—";
  const statusLabel = coin?.status === 1 ? "Active" : coin?.status === 0 ? "Inactive" : "Unknown";
  const statusColor = coin?.status === 1 ? "green" : coin?.status === 0 ? "red" : "default";

  // Column render overrides for columns that need JSX (Link, Tag)
  const columnOverrides = {
    user: (_, r) => {
      const name = [r?.userDetails?.firstName, r?.userDetails?.lastName].filter(Boolean).join(" ");
      const userId = r?.userId || r?.user;
      return userId
        ? <Link to={`/admin/user/profile/${userId}`} style={{ color: "#1677ff", fontWeight: 500 }}>{name || userId}</Link>
        : name || "—";
    },
    amount: (val, r) => r?.amount != null ? `${r.amount} ${coinSymbol}` : "—",
    reserved: (val, r) => r?.reserved != null ? `${r.reserved} ${coinSymbol}` : "—",
  };

  const tableColumns = [
    ...dynamicColumns.map((col) =>
      columnOverrides[col.key] ? { ...col, render: columnOverrides[col.key] } : col
    ),
  ];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">

          {/* ── Header ── */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Coin Detail</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/admin">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/admin/coin">Coins</Link></li>
                  <li className="breadcrumb-item active">{coinSymbol}</li>
                </ul>
              </div>
              <div className="col-auto">
                <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={() => history.goBack()}>
                  <FiArrowLeft /> Back
                </button>
              </div>
            </div>
          </div>

          {coinLoading ? (
            <div className="text-center py-5"><Spin size="large" /></div>
          ) : !coin ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <div style={{ fontSize: 48, marginBottom: 12 }}>🪙</div>
                <h4 style={{ marginBottom: 8 }}>
                  {coinError || "Coin not found"}
                </h4>
                <p style={{ color: "#888", marginBottom: 24 }}>
                  The coin you&apos;re looking for doesn&apos;t exist or has been removed.
                  <br />
                  <span style={{ fontSize: 13 }}>ID: <code>{id}</code></span>
                </p>
                <div className="d-flex justify-content-center gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => history.goBack()}
                  >
                    <FiArrowLeft /> Go Back
                  </button>
                  <Link to="/admin/coin" className="btn btn-primary">
                    View All Coins
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* ── Stat cards ── */}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
                <StatCard label="Coin" value={
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {coin?.image && <img src={coin.image} style={{ width: 24, height: 24, borderRadius: 12 }} alt={coinSymbol} />}
                        <span style={{ fontSize: 18 }}>{coinSymbol}</span>
                    </div>
                } color="#1677ff" />
                <StatCard label="Status" value={<Tag color={statusColor} style={{ fontSize: 13 }}>{statusLabel}</Tag>} />
                <StatCard label="Price" value={coin?.price ?? "—"} color="#1a7a4a" />
                <StatCard label="Deposit" value={<Tag color={coin?.depositEnabled ? "green" : "red"}>{coin?.depositEnabled ? "Enabled" : "Disabled"}</Tag>} />
                <StatCard label="Withdraw" value={<Tag color={coin?.withdrawEnabled ? "green" : "red"}>{coin?.withdrawEnabled ? "Enabled" : "Disabled"}</Tag>} />
                <StatCard label="Created" value={<span style={{ fontSize: 13 }}>{formatDateTime(coin?.createdAt)}</span>} />
              </div>

              <div className="row">
                {/* ── LEFT — coin info ── */}
                <div className="col-lg-4">

                  <Card title="Coin Details" icon="🪙">
                    <InfoRow label="ID" mono>{coin?._id}</InfoRow>
                    <InfoRow label="Name">{coin?.name}</InfoRow>
                    <InfoRow label="Symbol">{coin?.symbol}</InfoRow>
                    <InfoRow label="Price">{coin?.price}</InfoRow>
                    <InfoRow label="Chain Address" mono>{coin?.chain_address}</InfoRow>
                    <InfoRow label="Description">{coin?.description}</InfoRow>
                    <InfoRow label="Deposit">{coin?.depositEnabled ? "Enabled" : "Disabled"}</InfoRow>
                    <InfoRow label="Withdraw">{coin?.withdrawEnabled ? "Enabled" : "Disabled"}</InfoRow>
                    <InfoRow label="Status"><Tag color={statusColor}>{statusLabel}</Tag></InfoRow>
                    <InfoRow label="Created At">{formatDateTime(coin?.createdAt)}</InfoRow>
                    <InfoRow label="Updated At">{formatDateTime(coin?.updatedAt)}</InfoRow>
                  </Card>
                </div>

                {/* ── RIGHT — users with filters ── */}
                <div className="col-lg-8">
                  <div className="list-layout" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                    {/* users table card */}
                    <div className="card" style={{ flex: 1, minWidth: 0 }}>
                      <div className="card-body">
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>
                            Users Holding {coinSymbol}
                          </span>
                          {usersTotal > 0 && (
                            <span style={{ fontSize: 13, color: "#8c8c8c", marginLeft: 8 }}>
                              {usersTotal} total
                            </span>
                          )}
                        </div>

                        <TableToolbar
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          pageSizeOptions={pageSizeOptions}
                          total={usersTotal}
                          page={usersPage}
                          visibleColumnKeys={visibleColumnKeys}
                          allColumns={ALL_COLUMNS}
                          toggleColumn={toggleColumn}
                          resetColumns={resetColumns}
                          onDownload={handleDownload}
                          downloadLoading={downloadLoading}
                        />

                        <div className="table-responsive">
                          {usersLoading ? (
                            <div className="text-center py-4"><Spin /></div>
                          ) : (
                            <Table
                              size="small"
                              pagination={false}
                              rowKey={(r) => r?._id ?? r?.id}
                              dataSource={users}
                              columns={tableColumns}
                              scroll={{ x: "max-content" }}
                              locale={{ emptyText: "No users found for this coin." }}
                            />
                          )}
                          {usersTotal > pageSize && (
                            <CustomPagination
                              key={`pg-${pageSize}`}
                              totalTabs={usersTotal}
                              tabsPerPage={pageSize}
                              handleChange={(p) => setUsersPage(p)}
                              className={usersLoading ? "display-hide" : "display-flex"}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* filter panel */}
                    <div style={{ width: 220, flexShrink: 0 }}>
                      <FilterPanel
                        count={usersTotal}
                        onClear={clearFilters}
                      >
                        <FilterPanel.Field label="Search">
                          <input
                            type="text"
                            placeholder="User name or email"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(sanitizeInput(e.target.value))}
                          />
                        </FilterPanel.Field>

                        <FilterPanel.Field label="Wallet Type">
                          <Select
                            options={walletTypeOptions}
                            value={walletType}
                            onChange={setWalletType}
                            styles={selectStyles}
                            placeholder="Select Type"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </FilterPanel.Field>

                      </FilterPanel>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CoinDetail;
